import type { PublicDevice } from "./types";

export type SecurityVerificationState = "unknown" | "verified" | "changed";

export type StoredSecurityVerification = Readonly<{
  code: string;
  verifiedAt: string;
}>;

const STORAGE_PREFIX = "jtuc-live:security-verification:";

function canonicalParty(devices: readonly PublicDevice[]): string {
  return devices
    .map((device) => `${device.id}:${device.fingerprint}:${device.encryptionPublicKey}:${device.signingPublicKey}`)
    .sort((left, right) => left.localeCompare(right))
    .join("\n");
}

export function securityCodeInput(ownDevices: readonly PublicDevice[], peerDevices: readonly PublicDevice[]): string {
  const parties = [canonicalParty(ownDevices), canonicalParty(peerDevices)]
    .sort((left, right) => left.localeCompare(right));
  return ["JTUC-SAFETY-V2", `party:0\n${parties[0]}`, `party:1\n${parties[1]}`].join("\n");
}

export async function computeSecurityCode(ownDevices: readonly PublicDevice[], peerDevices: readonly PublicDevice[]): Promise<string> {
  const digest = await globalThis.crypto.subtle.digest("SHA-256", new TextEncoder().encode(securityCodeInput(ownDevices, peerDevices)));
  const hex = [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, "0")).join("").toUpperCase();
  return hex.match(/.{1,4}/g)?.join("-") ?? hex;
}

export function readSecurityVerification(chatId: string): StoredSecurityVerification | null {
  try {
    const raw = globalThis.localStorage?.getItem(`${STORAGE_PREFIX}${chatId}`);
    if (!raw) return null;
    const value = JSON.parse(raw) as Partial<StoredSecurityVerification>;
    if (typeof value.code !== "string" || !/^[0-9A-F]{4}(?:-[0-9A-F]{4}){15}$/.test(value.code) || typeof value.verifiedAt !== "string") return null;
    return { code: value.code, verifiedAt: value.verifiedAt };
  } catch {
    return null;
  }
}

export function securityVerificationState(chatId: string, code: string | null): SecurityVerificationState {
  if (!code) return "unknown";
  const stored = readSecurityVerification(chatId);
  if (!stored) return "unknown";
  return stored.code === code ? "verified" : "changed";
}

export function saveSecurityVerification(chatId: string, code: string): StoredSecurityVerification {
  const value = { code, verifiedAt: new Date().toISOString() } satisfies StoredSecurityVerification;
  try {
    globalThis.localStorage?.setItem(`${STORAGE_PREFIX}${chatId}`, JSON.stringify(value));
  } catch {
    // A blocked browser storage must not prevent the user from continuing the chat.
  }
  return value;
}
